import { useState, useEffect } from "react"
import { useSearchParams } from "react-router"
import VideoShare from "./components/pages/VideoShare/VideoShare"
import Preview from "./components/pages/VideoShare/Preview"
import Empty from "./components/pages/Empty/Empty"
import Processing from "./components/pages/Processing/Processing"
import Layout from "./components/Layout"
import Loading from "./components/pages/Loading"
import { shareVideo } from "./helpers/shareVideo"
// import TestMenu from "./components/TestMenu"

export function App() {
  const [page, setPage] = useState("default") // empty, processing
  const [isLoading, setLoading] = useState(true)
  const [urlHash, setUrlHash] = useState(null)
  const [cachedVideoBlob, setCachedVideoBlob] = useState(null)
  const videoPreviewUrl = `https://holobooth.hypervsn.com/video/${urlHash}.mp4`
  const [searchParams] = useSearchParams() // Hook to access query parameters
  useEffect(() => {
    // Get the value of the "hash" parameter
    const hashValue = searchParams.get("h")
    if (hashValue) {
      setUrlHash(hashValue)
      const videoUrl = `https://holobooth.hypervsn.com/video/${hashValue}.mp4`
      // console.log(`hash: ${hashValue}`)
      console.log("🔗 videoUrl", videoUrl)
    } else {
      console.log("🚨 hash parameter not found in URL")
      setLoading(false)
      setPage("empty")
    }
  }, [urlHash])

  const checkVideo = async () => {
    const videoUrl = `https://holobooth.hypervsn.com/video/${urlHash}.mp4`
    // Use a <video> element to probe availability without triggering a CORS preflight.
    // A HEAD fetch to another origin can be blocked if the server doesn't send
    // Access-Control-Allow-Origin. Loading metadata on a video element relies on
    // media element fetches and will fire `loadedmetadata` or `error` which we
    // can use to detect readiness in most browsers without causing a CORS preflight.
    return new Promise((resolve) => {
      let settled = false
      const video = document.createElement("video")
      video.preload = "metadata"
      video.src = videoUrl

      const cleanup = () => {
        video.pause()
        video.removeAttribute("src")
        video.load()
        video.remove()
        settled = true
      }

      const onReady = () => {
        if (settled) return
        console.log("✅ Video is ready (loadedmetadata)")
        setLoading(false)
        if (page === "processing") setPage("default")

        cleanup()
        resolve(true)
      }

      const onError = (e) => {
        if (settled) return
        // If the server returns 404 or otherwise, the media element will error.
        console.log("🤔 Video not ready / error loading video", e)
        // Distinguish between not found (empty) and still processing is not
        // always possible from the media element alone. We'll treat errors as
        // "processing" first, and fall back to empty after repeated failures.
        setPage("processing")
        setLoading(false)
        cleanup()
        resolve(false)
      }

      // Timeout in case the request hangs (network issues). Treat as error.
      const timeoutId = setTimeout(() => {
        if (settled) return
        console.log("⏱️ Video probe timed out")
        onError(new Error("timeout"))
      }, 15000)

      video.addEventListener("loadedmetadata", () => {
        clearTimeout(timeoutId)
        onReady()
      })
      video.addEventListener("error", (e) => {
        clearTimeout(timeoutId)
        onError(e)
      })

      // Start loading metadata
      // Some browsers start loading only after appending to DOM; append briefly.
      // We append invisibly to avoid layout shifts.
      video.style.width = "0"
      video.style.height = "0"
      video.style.position = "absolute"
      video.style.left = "-9999px"
      document.body.appendChild(video)
      // Calling load triggers the media network requests in many browsers.
      try {
        video.load()
      } catch (e) {
        // Some browsers may throw; fall back to onError
        clearTimeout(timeoutId)
        onError(e)
      }
    })
  }

  // Add new function to fetch and cache video
  const cacheVideo = async () => {
    if (!videoPreviewUrl) {
      console.warn("⚠️ No videoPreviewUrl provided — skipping cache.")
      return
    }
    try {
      const response = await fetch(videoPreviewUrl)

      if (!response.ok) {
        console.warn(`⚠️ Video fetch failed with status: ${response.status}`)
        return
      }

      const blob = await response.blob()
      setCachedVideoBlob(blob)
      console.log("✅ Video cached successfully")
    } catch (error) {
      console.error("❌ Error caching video:", error)
    }
  }

  // Modify the existing checkVideo useEffect to also cache the video when ready
  useEffect(() => {
    if (urlHash) {
      checkVideo().then((isReady) => {
        if (isReady) {
          console.log("📦 Cache video")
          cacheVideo()
        }
      })
    }
  }, [urlHash])

  useEffect(() => {
    if (page === "processing") {
      setTimeout(() => {
        console.log("⏱️ Check video after 6 seconds")
        checkVideo()
        console.log("⏱️ Check video in 10 seconds...")
        const waitProcessing = setInterval(() => checkVideo(), 10000)
        return () => clearInterval(waitProcessing)
      }, 6000)
    }
  }, [page])

  const handleShareVideo = async () => {
    if (cachedVideoBlob) return shareVideo(urlHash, cachedVideoBlob)
    else return shareVideo(urlHash)
  }

  if (isLoading) return <Loading />
  const isCaching = !!!cachedVideoBlob

  return (
    <>
      {/* <TestMenu setPage={setPage} page={page} /> */}
      <Layout>
        {!(page === "empty") && (
          <Preview
            page={page}
            videoUrl={videoPreviewUrl}
            cachedVideoBlob={cachedVideoBlob}
            isCaching={isCaching}
          />
        )}
        {page === "default" && (
          <VideoShare shareVideo={handleShareVideo} isCaching={isCaching} />
        )}
        {page === "empty" && <Empty />}
        {page === "processing" && <Processing />}
      </Layout>
    </>
  )
}
